import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Drawer,
  Form,
  Input,
  Switch,
  Space,
  message,
  Pagination,
  Select,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { FiSearch } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../config/permissions';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import whatsappChatbotContactService from '../../services/whatsappChatbotContactService';
import {
  getWhatsAppChatbotContactColumns,
  ACTIVE_FILTER_OPTIONS,
} from './commons/whatsappChatbotContacts.helpers';

export default function WhatsAppChatbotContactsPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canCreate = isSuperAdmin || hasPermission(PERMISSIONS.PRICING_WHATSAPP_CHATBOT_CREATE);
  const canEdit = isSuperAdmin || hasPermission(PERMISSIONS.PRICING_WHATSAPP_CHATBOT_EDIT);

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [total, setTotal] = useState(0);
  const [form] = Form.useForm();

  const isEdit = Boolean(editing);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const { contacts: list, pagination } = await whatsappChatbotContactService.list({
        search: searchInput.trim() || undefined,
        is_active: activeFilter || undefined,
        page,
        limit: pageSize,
      });
      setContacts(list);
      setTotal(pagination?.total ?? list.length);
    } catch (e) {
      message.error(e?.message || 'Failed to load chatbot contacts');
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [searchInput, activeFilter, page, pageSize]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, chatbot_enabled: false });
    setDrawerOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      phone_number: record.phone_number,
      display_name: record.display_name,
      is_active: record.is_active,
      chatbot_enabled: record.chatbot_enabled,
    });
    setDrawerOpen(true);
  };

  const onDrawerClose = () => {
    setDrawerOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const payload = {
        phone_number: values.phone_number.trim(),
        display_name: values.display_name?.trim() || null,
        is_active: values.is_active,
        chatbot_enabled: values.chatbot_enabled,
      };

      if (isEdit) {
        const res = await whatsappChatbotContactService.update(editing.id, payload);
        if (res?.success === false) {
          message.error(res?.message || 'Update failed');
          return;
        }
        message.success('Contact updated');
      } else {
        const res = await whatsappChatbotContactService.create(payload);
        if (res?.success === false) {
          message.error(res?.message || 'Create failed');
          return;
        }
        message.success('Contact added');
      }

      onDrawerClose();
      loadContacts();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (record) => {
    try {
      const res = await whatsappChatbotContactService.remove(record.id);
      if (res?.success === false) {
        message.error(res?.message || 'Delete failed');
        return;
      }
      message.success('Contact removed');
      loadContacts();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Delete failed');
    }
  };

  const onToggleChatbot = async (record) => {
    try {
      const res = await whatsappChatbotContactService.update(record.id, {
        chatbot_enabled: !record.chatbot_enabled,
      });
      if (res?.success === false) {
        message.error(res?.message || 'Update failed');
        return;
      }
      loadContacts();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Update failed');
    }
  };

  const columns = getWhatsAppChatbotContactColumns({
    canEdit,
    onEdit: openEdit,
    onDelete,
    onToggleChatbot,
  });

  return (
    <DashboardPageWrapper>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">WhatsApp chatbot contacts</h1>
          <p className="text-sm text-gray-500 mt-1">
            Allowlisted individual numbers that can DM the price and investor-note chatbot.
          </p>
        </div>
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add contact
          </Button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-3 bg-gray-50/50">
          <div className="relative w-full sm:max-w-md">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search phone or name"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            />
          </div>
          <Select
            value={activeFilter}
            onChange={(v) => {
              setActiveFilter(v);
              setPage(1);
            }}
            options={ACTIVE_FILTER_OPTIONS}
            className="w-full sm:w-40"
          />
          <Button icon={<ReloadOutlined />} onClick={loadContacts}>
            Refresh
          </Button>
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={contacts}
          loading={loading}
          pagination={false}
          scroll={{ x: 720 }}
        />

        <div className="p-4 border-t border-gray-100 flex justify-end">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger
            onChange={(p, size) => {
              setPage(p);
              setPageSize(size);
            }}
          />
        </div>
      </div>

      <Drawer
        title={isEdit ? 'Edit contact' : 'Add contact'}
        width={420}
        open={drawerOpen}
        onClose={onDrawerClose}
        extra={
          <Space>
            <Button onClick={onDrawerClose}>Cancel</Button>
            <Button type="primary" loading={submitting} onClick={onSubmit}>
              {isEdit ? 'Save' : 'Add'}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="phone_number"
            label="Phone number"
            rules={[{ required: true, message: 'Enter phone number with country code' }]}
          >
            <Input placeholder="919876543210" maxLength={32} disabled={isEdit} />
          </Form.Item>

          <Form.Item name="display_name" label="Display name">
            <Input maxLength={255} placeholder="Optional label for admin" />
          </Form.Item>

          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>

          <Form.Item
            name="chatbot_enabled"
            label="Chatbot enabled"
            valuePropName="checked"
            extra="When on, this number can query prices and investor notes via DM."
          >
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Drawer>
    </DashboardPageWrapper>
  );
}
